import React, { useContext, useEffect, useState } from "react";
import "./index.css";
import { AppContext } from "../../context/AppContext";

const PolicyTabs = () => {
  const [activeTab, setActiveTab] = useState("terms");
  const { setActiveRoute, setActiveSection } = useContext(AppContext);

  useEffect(() => {
    setActiveRoute("/terms-and-privacy");
    setActiveSection("");
  }, [setActiveRoute, setActiveSection]);

  // Smooth scroll to top when changing tabs
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const PolicySection = ({ title, children }) => (
    <div className="policy-section">
      <h3 className="policy-section-title">{title}</h3>
      <div className="policy-section-content">{children}</div>
    </div>
  );

  const termsInfo = () => {
    return (
      <div className="policy-page">
        <div className="policy-header">
          <h1 className="policy-main-title">Terms & Conditions</h1>
          <p className="policy-last-updated">Last Updated: March 2024</p>
        </div>

        <div className="policy-summary-card">
          <p className="policy-summary-text">
            Welcome to IBIX Qodify Technologies. By enrolling in our programs,
            you agree to comply with and be bound by the following terms and
            conditions. Please review them carefully.
          </p>
        </div>

        <PolicySection title="1. Scope & Acceptance">
          <p>
            When a student registers for any IBIX course, it signifies their
            acceptance of all the rules and policies outlined here. This
            document acts as a mutual agreement between IBIX Qodify Technologies
            and the student. It governs all interactions, responsibilities, and
            expectations during the learning journey. Continued use of IBIX
            services after updates implies agreement to the revised terms, and
            students are advised to review them periodically. Acceptance is not
            limited to digital acknowledgment alone; by attending classes or
            engaging with IBIX resources, students provide their implicit
            agreement to all terms.
          </p>
        </PolicySection>

        <PolicySection title="2. Program Offerings">
          <p>
            IBIX offers carefully curated learning paths, each designed to serve
            different learning goals, educational backgrounds, and career
            ambitions.
          </p>
          <div className="program-cards">
            <div className="program-card">
              <div className="program-tag smart">Smart Track</div>
              <p>
                Suited for beginners, this track introduces foundational
                concepts of full-stack development. It focuses on building the
                core understanding of HTML, CSS, JavaScript, and backend
                fundamentals through interactive assignments and
                beginner-friendly projects.
              </p>
            </div>
            <div className="program-card">
              <div className="program-tag pro">Pro Track</div>
              <p>
                Tailored for individuals with prior knowledge in tech or coding,
                this program dives deeper into full-stack frameworks, advanced
                modules, and real-world applications using React, Node.js,
                MongoDB, and Docker.
              </p>
            </div>
            <div className="program-card">
              <div className="program-tag tools">Tools Track</div>
              <p>
                Ideal for students seeking non-coding roles such as ServiceNow
                administrators, QA testers, or technical support engineers.
                Includes hands-on experience with Jira, Selenium, ServiceNow,
                and Postman.
              </p>
            </div>
          </div>
        </PolicySection>

        <PolicySection title="3. Registration & Enrollment">
          <p>
            To begin the IBIX journey, students must submit an accurate and
            complete registration form. Enrollment is confirmed when:
          </p>
          <ul className="policy-list">
            <li>The student pays the full course fee, or</li>
            <li>
              A reservation amount of ₹2,000 is paid using IBIX-authorized
              payment methods.
            </li>
          </ul>
          <div className="important-note">
            <strong>⚠️ Important:</strong> Payments made to personal UPI IDs or
            unofficial channels will not be recognized.
          </div>
        </PolicySection>

        <PolicySection title="4. Payment Terms & Invoices">
          <p>IBIX only accepts payments through official channels:</p>
          <ul className="policy-list">
            <li>Official bank details</li>
            <li>Company QR codes</li>
            <li>Secure payment gateways</li>
          </ul>
          <p>Once payment is confirmed:</p>
          <ul className="policy-list">
            <li>
              An official receipt/invoice will be issued within 48 working hours
            </li>
            <li>
              If the invoice is not received, notify the finance support team
            </li>
            <li>Keep payment details for future reference</li>
          </ul>
        </PolicySection>

        <PolicySection title="5. Access & Onboarding">
          <p>Within 1–2 working days of successful enrollment:</p>
          <ul className="policy-list">
            <li>Receive LMS access credentials</li>
            <li>Join official student groups</li>
            <li>Get assigned a personal mentor</li>
          </ul>
          <p>Requirements:</p>
          <ul className="policy-list">
            <li>Attend at least 80% of classes</li>
            <li>Regular interaction with mentors</li>
            <li>Active participation in LMS activities</li>
          </ul>
        </PolicySection>

        <PolicySection title="6. Refund & Cancellation">
          <h4 className="policy-subtitle">a. Refund Eligibility</h4>
          <ul className="policy-list">
            <li>Request within 7 calendar days from joining date</li>
            <li>Less than 7 sessions accessed</li>
            <li>Full course fee paid in advance</li>
            <li>
              ₹2,000 administrative fee deducted, remainder refunded within 7–10
              working days
            </li>
          </ul>

          <h4 className="policy-subtitle">b. Non-Refundable Conditions</h4>
          <ul className="policy-list">
            <li>More than 7 sessions attended</li>
            <li>Request beyond 7-day window</li>
            <li>₹2,000 reservation fee only</li>
            <li>Voluntary dropout</li>
          </ul>
        </PolicySection>

        {/* Continue with remaining sections in same pattern */}
        <PolicySection title="7. Code of Conduct">
          <ul className="policy-list">
            <li>Be punctual to all sessions</li>
            <li>Maintain professional communication</li>
            <li>No spamming or disruptive behavior</li>
            <li>Never share login credentials</li>
          </ul>
        </PolicySection>

        {/* ... Continue with all sections ... */}

        <div className="policy-footer">
          <p>
            By enrolling in an IBIX program, you acknowledge that you have read,
            understood, and agree to be bound by these Terms & Conditions.
          </p>
          <div className="policy-footer-actions">
            <button className="btn btn-primary" onClick={() => window.print()}>
              Print Terms
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => (window.location.href = "mailto:support@ibix.com")}
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  };

  const privacyInfo = () => {
    return (
      <div className="policy-page">
        <div className="policy-header">
          <h1 className="policy-main-title">Privacy Policy</h1>
          <p className="policy-last-updated">Last Updated: March 2024</p>
        </div>

        <div className="policy-summary-card">
          <p className="policy-summary-text">
            At IBIX Qodify Technologies, we take your privacy seriously. This
            policy describes how we collect, use, and protect your personal
            information.
          </p>
        </div>

        <PolicySection title="1. Information We Collect">
          <ul className="policy-list">
            <li>
              <strong>Personal Identification:</strong> Name, email, phone,
              address, date of birth
            </li>
            <li>
              <strong>Payment Details:</strong> Bank info, UPI IDs, transaction
              IDs
            </li>
            <li>
              <strong>Academic Data:</strong> Course enrollments, attendance,
              performance
            </li>
            <li>
              <strong>Technical Data:</strong> IP address, browser type, device
              info
            </li>
            <li>
              <strong>Communications:</strong> Support queries, feedback,
              interactions
            </li>
          </ul>
        </PolicySection>

        <PolicySection title="2. How We Use Your Information">
          <ul className="policy-list">
            <li>Enable course access and authentication</li>
            <li>Provide academic content and support</li>
            <li>Monitor learning progress</li>
            <li>Send important updates and notifications</li>
            <li>Process payments</li>
            <li>Improve platform and services</li>
          </ul>
        </PolicySection>

        <PolicySection title="3. Data Sharing">
          <p>We do not sell or rent your data. Limited sharing occurs with:</p>
          <ul className="policy-list">
            <li>Authorized service providers (hosting, payments, email)</li>
            <li>Placement partners (with your explicit consent)</li>
            <li>Regulatory authorities (when required by law)</li>
          </ul>
        </PolicySection>

        <PolicySection title="4. Data Security">
          <p>We employ industry-standard security measures including:</p>
          <ul className="policy-list">
            <li>Data encryption</li>
            <li>Multi-layered firewalls</li>
            <li>Access controls</li>
            <li>Continuous monitoring</li>
          </ul>
        </PolicySection>

        <PolicySection title="5. Your Rights">
          <ul className="policy-list">
            <li>Request copy of your data</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion (subject to legal requirements)</li>
            <li>Withdraw consent for communications</li>
          </ul>
        </PolicySection>

        <PolicySection title="6. Cookies & Tracking">
          <p>We use cookies to:</p>
          <ul className="policy-list">
            <li>Store preferences</li>
            <li>Track usage patterns</li>
            <li>Analyze traffic</li>
          </ul>
          <p>You can disable cookies in browser settings.</p>
        </PolicySection>

        <div className="policy-footer">
          <p>By using IBIX services, you consent to this Privacy Policy.</p>
          <div className="policy-footer-actions">
            <button className="btn btn-primary" onClick={() => window.print()}>
              Print Policy
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => (window.location.href = "mailto:privacy@ibix.com")}
            >
              Privacy Questions
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="policy-tabs-container">
      <div className="policy-tabs-wrapper">
        <div className="policy-tabs-header">
          <h2 className="policy-header-title">Legal Information</h2>
          <p className="policy-header-subtitle">
            Review our terms and privacy practices
          </p>
        </div>

        <div className="policy-tabs-nav">
          <button
            className={`policy-tab-btn ${activeTab === "terms" ? "active" : ""}`}
            onClick={() => setActiveTab("terms")}
          >
            <span className="tab-icon">📄</span>
            Terms & Conditions
          </button>
          <button
            className={`policy-tab-btn ${activeTab === "privacy" ? "active" : ""}`}
            onClick={() => setActiveTab("privacy")}
          >
            <span className="tab-icon">🔒</span>
            Privacy Policy
          </button>
        </div>

        <div className="policy-content-area">
          {activeTab === "terms" && termsInfo()}
          {activeTab === "privacy" && privacyInfo()}
        </div>
      </div>
    </div>
  );
};

export default PolicyTabs;
